<script setup lang="ts">
import { ref, onMounted } from 'vue'
import { useRoute } from 'vue-router'
import { useI18n } from 'vue-i18n'
import type { PublicVolunteerSignUp } from '@/types/volunteer'
import { getConfirmation } from '@/api/publicVolunteer'
import AppFooter from '@/components/layout/AppFooter.vue'

const route = useRoute()
const { t } = useI18n()

const confirmationToken = String(route.params['confirmationToken'])

const loading = ref(true)
const loadError = ref<string | null>(null)
const signUp = ref<PublicVolunteerSignUp | null>(null)

function formatTime(time: string): string {
  return time.length > 5 ? time.slice(0, 5) : time
}

function formatDate(dateStr: string): string {
  return new Date(dateStr + 'T00:00:00').toLocaleDateString(undefined, {
    weekday: 'short',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  })
}

onMounted(async () => {
  try {
    signUp.value = await getConfirmation(confirmationToken)
  } catch {
    loadError.value = t('volunteer.confirmation.heading')
  } finally {
    loading.value = false
  }
})
</script>

<template>
  <div class="min-h-screen bg-slate-50 flex flex-col">
    <!-- Loading -->
    <div v-if="loading" class="flex items-center justify-center min-h-screen">
      <svg class="animate-spin h-10 w-10 text-sky-500" fill="none" viewBox="0 0 24 24">
        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" />
        <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
      </svg>
    </div>

    <!-- Error -->
    <div
      v-else-if="loadError || !signUp"
      class="flex items-center justify-center min-h-screen px-4"
    >
      <div class="max-w-md w-full text-center bg-white rounded-2xl border border-slate-200 p-10">
        <div class="w-14 h-14 rounded-full bg-slate-100 flex items-center justify-center mx-auto mb-4">
          <svg class="w-7 h-7 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        </div>
        <h1 class="text-xl font-bold text-slate-900 mb-4">{{ t('volunteer.confirmation.heading') }}</h1>
        <a href="/" class="inline-flex items-center gap-1.5 text-sm font-medium text-sky-600 hover:text-sky-700 transition-colors">
          &larr; {{ t('common.back') }}
        </a>
      </div>
    </div>

    <!-- Confirmation content -->
    <template v-else>
      <!-- Header -->
      <div class="bg-white border-b border-slate-200">
        <div class="max-w-2xl mx-auto px-4 sm:px-6 py-6">
          <div class="flex items-center gap-2 mb-1">
            <span class="inline-flex items-center rounded-full bg-sky-100 px-2.5 py-0.5 text-xs font-semibold uppercase tracking-wider text-sky-700">
              {{ t('volunteer.wizard.header.pill') }}
            </span>
          </div>
          <h1 class="text-xl font-bold text-slate-900">{{ signUp.event.name }}</h1>
        </div>
      </div>

      <div class="max-w-2xl mx-auto w-full px-4 sm:px-6 py-8 flex-1 space-y-6">
        <!-- Success banner -->
        <div class="rounded-2xl bg-sky-50 border border-sky-200 px-6 py-8 text-center">
          <div class="w-16 h-16 rounded-full bg-sky-100 flex items-center justify-center mx-auto mb-4">
            <svg class="w-8 h-8 text-sky-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h2 class="text-2xl font-bold text-sky-800 mb-2">{{ t('volunteer.confirmation.heading') }}</h2>
          <p class="text-sm text-sky-700">{{ t('volunteer.confirmation.body', { firstName: signUp.first_name }) }}</p>
        </div>

        <!-- Reference card -->
        <div class="bg-white rounded-2xl border border-slate-200 p-6 space-y-4">
          <div>
            <p class="text-xs font-medium text-slate-500 uppercase tracking-wide mb-0.5">
              {{ t('volunteer.confirmation.reference_code_label') }}
            </p>
            <p class="text-2xl font-bold text-sky-700 font-mono tracking-wider">
              {{ signUp.reference_code }}
            </p>
          </div>

          <!-- Slots -->
          <div v-if="signUp.slots.length > 0" class="border-t border-slate-100 pt-4">
            <p class="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">
              {{ t('volunteer.wizard.step2.slot_heading') }}
            </p>
            <ul class="space-y-1">
              <li v-for="(slot, idx) in signUp.slots" :key="idx" class="text-sm text-slate-700">
                {{ formatDate(slot.slot_date) }} · {{ formatTime(slot.start_time) }}–{{ formatTime(slot.end_time) }}
              </li>
            </ul>
          </div>

          <!-- Roles -->
          <div v-if="signUp.roles.length > 0" class="border-t border-slate-100 pt-4">
            <p class="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">
              {{ t('volunteer.wizard.step2.role_heading') }}
            </p>
            <ul class="space-y-1">
              <li v-for="(role, idx) in signUp.roles" :key="idx" class="text-sm text-slate-700">
                {{ role.name }}
              </li>
            </ul>
          </div>
        </div>

        <!-- Account link CTA (placeholder — linking feature is out of scope in v1) -->
        <p class="text-sm text-slate-500 text-center">
          {{ t('volunteer.confirmation.link_account_cta') }}
        </p>
      </div>

      <AppFooter />
    </template>
  </div>
</template>
